import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  Link,
  IconButton,
  Button,
  Snackbar
} from '@mui/material';
import { Description as DescriptionIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getAllFixVersions, deleteReleaseNotes } from '../../services/apiService';
import { formatDate } from '../../utils/formatters';
import Loading from '../common/Loading';

/**
 * ReleaseNotesView component for displaying links to all generated release notes
 * @returns {JSX.Element} ReleaseNotesView component
 */
const ReleaseNotesView = () => {
  // State for fix versions
  const [fixVersions, setFixVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Fetch fix versions on component mount
  useEffect(() => {
    const fetchFixVersions = async () => {
      try {
        setLoading(true);
        const response = await getAllFixVersions();
        
        console.log('Fix versions response:', response);
        
        // Process the response to extract fix versions with release notes
        let versionsWithReleaseNotes = [];
        
        // Check for different response formats
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          // Nested data structure (response.data.data is an array)
          versionsWithReleaseNotes = response.data.data.filter(fixVersion => 
            fixVersion.active && fixVersion.releaseNotesUrl
          );
        } else if (response && response.data && Array.isArray(response.data)) {
          // Direct array in data property
          versionsWithReleaseNotes = response.data.filter(fixVersion => 
            fixVersion.active && fixVersion.releaseNotesUrl
          );
        } else if (response && Array.isArray(response)) {
          // Direct array response
          versionsWithReleaseNotes = response.filter(fixVersion => 
            fixVersion.active && fixVersion.releaseNotesUrl
          );
        } else if (response && response.success && Array.isArray(response.data)) {
          // Success object with data array
          versionsWithReleaseNotes = response.data.filter(fixVersion => 
            fixVersion.active && fixVersion.releaseNotesUrl
          );
        } else {
          console.error('Invalid fix versions response:', response);
          setError('Failed to load fix versions. Invalid response format.');
          setFixVersions([]); // Initialize with empty array to prevent map errors
        }
        
        // Sort versions by version number (descending)
        versionsWithReleaseNotes.sort((a, b) => {
          // Parse version strings like "5.9.08" into components
          const aComponents = a.version.split('.').map(Number);
          const bComponents = b.version.split('.').map(Number);
          
          // Compare major version
          if (aComponents[0] !== bComponents[0]) {
            return bComponents[0] - aComponents[0];
          }
          
          // Compare minor version
          if (aComponents[1] !== bComponents[1]) {
            return bComponents[1] - aComponents[1];
          }
          
          // Compare patch version
          return bComponents[2] - aComponents[2];
        });
        
        setFixVersions(versionsWithReleaseNotes);
      } catch (err) {
        console.error('Error fetching fix versions:', err);
        setError('Failed to load fix versions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFixVersions();
  }, []);
  
  // Handle view release notes
  const handleViewReleaseNotes = (url) => {
    if (url.startsWith('OPEN_NEW_TAB:')) {
      // Extract the actual URL from the special format
      const actualUrl = url.replace('OPEN_NEW_TAB:', '');
      // Open in a new tab
      window.open(actualUrl, '_blank');
    } else {
      // Fallback to normal URL
      window.open(url, '_blank');
    }
  };
  
  // Handle delete release notes
  const handleDeleteReleaseNotes = async (fixVersionId, event) => {
    // Stop event propagation to prevent opening the release notes
    event.stopPropagation();
    
    try {
      // Confirm deletion
      if (!window.confirm('Are you sure you want to delete these release notes? This action cannot be undone.')) {
        return;
      }
      
      setLoading(true);
      
      // Delete release notes
      const response = await deleteReleaseNotes(fixVersionId);
      
      if (response && response.success) {
        // Remove the deleted release notes from the list
        setFixVersions(prevVersions => prevVersions.filter(version => version._id !== fixVersionId));
        
        setSnackbarMessage('Release notes deleted successfully!');
        setSnackbarOpen(true);
      } else {
        throw new Error('Failed to delete release notes');
      }
    } catch (error) {
      console.error('Error deleting release notes:', error);
      setSnackbarMessage('Failed to delete release notes. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Release Notes
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View release notes for all HighQ releases
        </Typography>
      </Box>
      
      {loading ? (
        <Loading message="Loading release notes..." />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Paper elevation={2} sx={{ p: 2 }}>
          {fixVersions.length > 0 ? (
            <List>
              {fixVersions.map((fixVersion) => (
                <React.Fragment key={fixVersion._id}>
                  <ListItem button onClick={() => handleViewReleaseNotes(fixVersion.releaseNotesUrl)}>
                    <ListItemIcon>
                      <DescriptionIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={`HighQ Release Notes - ${fixVersion.version}`}
                      secondary={`Release Date: ${fixVersion.releaseDate ? formatDate(fixVersion.releaseDate) : 'Not available'}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        color="error"
                        onClick={(e) => handleDeleteReleaseNotes(fixVersion._id, e)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography>No release notes available. Generate release notes from the Releases page.</Typography>
          )}
        </Paper>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ReleaseNotesView;
